import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import {
    settingsApi,
    Settings,
    Backup,
    TariffCards,
    UpdateSettingsData,
    UpdateTariffCardData,
    TariffCardType
} from '../../services/settingsApi';

interface SettingsState {
    settings: Settings | null;
    backups: Backup[];
    tariffCards: TariffCards | null;
    loading: boolean;
    backupsLoading: boolean;
    tariffLoading: boolean;
    error: string | null;
}

const initialState: SettingsState = {
    settings: null,
    backups: [],
    tariffCards: null,
    loading: false,
    backupsLoading: false,
    tariffLoading: false,
    error: null,
};

// Async thunks for Settings
export const fetchSettings = createAsyncThunk(
    'settings/fetchSettings',
    async (_, { rejectWithValue }) => {
        try {
            const response = await settingsApi.getSettings();
            return response;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch settings');
        }
    }
);

export const updateSettings = createAsyncThunk(
    'settings/updateSettings',
    async (data: UpdateSettingsData, { rejectWithValue }) => {
        try {
            const response = await settingsApi.updateSettings(data);
            return response;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to update settings');
        }
    }
);

// Async thunks for Backups
export const fetchBackups = createAsyncThunk(
    'settings/fetchBackups',
    async (_, { rejectWithValue }) => {
        try {
            const response = await settingsApi.getBackups();
            return response;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch backups');
        }
    }
);

export const createBackup = createAsyncThunk(
    'settings/createBackup',
    async (_, { rejectWithValue, dispatch }) => {
        try {
            const filename = await settingsApi.createBackup();
            // Refresh backups list after creating
            dispatch(fetchBackups());
            return filename;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to create backup');
        }
    }
);

export const downloadBackup = createAsyncThunk(
    'settings/downloadBackup',
    async (filename: string, { rejectWithValue }) => {
        try {
            await settingsApi.downloadBackup(filename);
            return filename;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to download backup');
        }
    }
);

export const deleteBackup = createAsyncThunk(
    'settings/deleteBackup',
    async (filename: string, { rejectWithValue, dispatch }) => {
        try {
            await settingsApi.deleteBackup(filename);
            // Refresh backups list after deleting
            dispatch(fetchBackups());
            return filename;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to delete backup');
        }
    }
);

// Async thunks for Tariff Cards
export const fetchTariffCards = createAsyncThunk(
    'settings/fetchTariffCards',
    async (_, { rejectWithValue }) => {
        try {
            const response = await settingsApi.getTariffCards();
            return response;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch tariff cards');
        }
    }
);

export const updateTariffCard = createAsyncThunk(
    'settings/updateTariffCard',
    async ({ type, data }: { type: TariffCardType; data: UpdateTariffCardData }, { rejectWithValue, dispatch }) => {
        try {
            await settingsApi.updateTariffCard(type, data);
            // Refresh all tariff cards after updating
            dispatch(fetchTariffCards());
            return type;
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to update tariff card');
        }
    }
);

const settingsSlice = createSlice({
    name: 'settings',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        // Fetch Settings
        builder
            .addCase(fetchSettings.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchSettings.fulfilled, (state, action) => {
                state.loading = false;
                state.settings = action.payload;
            })
            .addCase(fetchSettings.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Update Settings
        builder
            .addCase(updateSettings.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateSettings.fulfilled, (state, action) => {
                state.loading = false;
                state.settings = action.payload;
            })
            .addCase(updateSettings.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });

        // Fetch Backups
        builder
            .addCase(fetchBackups.pending, (state) => {
                state.backupsLoading = true;
                state.error = null;
            })
            .addCase(fetchBackups.fulfilled, (state, action) => {
                state.backupsLoading = false;
                state.backups = action.payload;
            })
            .addCase(fetchBackups.rejected, (state, action) => {
                state.backupsLoading = false;
                state.error = action.payload as string;
            });

        // Create Backup
        builder
            .addCase(createBackup.pending, (state) => {
                state.backupsLoading = true;
                state.error = null;
            })
            .addCase(createBackup.fulfilled, (state) => {
                state.backupsLoading = false;
            })
            .addCase(createBackup.rejected, (state, action) => {
                state.backupsLoading = false;
                state.error = action.payload as string;
            });

        // Download Backup
        builder
            .addCase(downloadBackup.pending, (state) => {
                state.error = null;
            })
            .addCase(downloadBackup.fulfilled, (state) => {
                // No state change needed
            })
            .addCase(downloadBackup.rejected, (state, action) => {
                state.error = action.payload as string;
            });

        // Delete Backup
        builder
            .addCase(deleteBackup.pending, (state) => {
                state.backupsLoading = true;
                state.error = null;
            })
            .addCase(deleteBackup.fulfilled, (state) => {
                state.backupsLoading = false;
            })
            .addCase(deleteBackup.rejected, (state, action) => {
                state.backupsLoading = false;
                state.error = action.payload as string;
            });

        // Fetch Tariff Cards
        builder
            .addCase(fetchTariffCards.pending, (state) => {
                state.tariffLoading = true;
                state.error = null;
            })
            .addCase(fetchTariffCards.fulfilled, (state, action) => {
                state.tariffLoading = false;
                state.tariffCards = action.payload;
            })
            .addCase(fetchTariffCards.rejected, (state, action) => {
                state.tariffLoading = false;
                state.error = action.payload as string;
            });

        // Update Tariff Card
        builder
            .addCase(updateTariffCard.pending, (state) => {
                state.tariffLoading = true;
                state.error = null;
            })
            .addCase(updateTariffCard.fulfilled, (state) => {
                state.tariffLoading = false;
            })
            .addCase(updateTariffCard.rejected, (state, action) => {
                state.tariffLoading = false;
                state.error = action.payload as string;
            });
    },
});

export const { clearError } = settingsSlice.actions;
export default settingsSlice.reducer;
